import {
  Header,
  IconAdd,
  ModalDialog,
  PrimaryButton,
  Space,
  Text,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import {
  type TermsMethodsKindEnum,
  TermsMethodsKinds,
} from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
import { CreateTermOrMethodFormPresenter } from '@webapp/modules/purchasing/features/create-term-or-method/presenters/CreateTermOrMethodFormPresenter'
import { EditableTermOrMethodTextFieldPresenter } from '@webapp/modules/purchasing/features/edit-term-or-method/presenters/EditableTermOrMethodTextFieldPresenter'
import { type TermOrMethod } from '@webapp/modules/purchasing/features/list-terms-and-methods/models'
import { TermsMethodsListPresenter } from '@webapp/modules/purchasing/features/list-terms-and-methods/presenters/TermsMethodsListPresenter'
import messages from './messages'
import { usePOTermsMethodsView } from './usePOTermsMethodsView'

export interface IPOTermsMethodsViewProps {
  /** Selects which of the three tabs this parameterized section serves */
  kind: TermsMethodsKindEnum
}

// The single place kind fans out to FS copy — features receive `kind` itself
const KIND_MESSAGES = {
  [TermsMethodsKinds.SHIPPING_TERMS]: {
    heading: messages.headingShippingTerms,
    description: messages.descriptionShippingTerms,
    newButton: messages.newButtonShippingTerms,
    createModalTitle: messages.createModalTitleShippingTerms,
  },
  [TermsMethodsKinds.PAYMENT_TERMS]: {
    heading: messages.headingPaymentTerms,
    description: messages.descriptionPaymentTerms,
    newButton: messages.newButtonPaymentTerms,
    createModalTitle: messages.createModalTitlePaymentTerms,
  },
  [TermsMethodsKinds.SHIPPING_METHODS]: {
    heading: messages.headingShippingMethods,
    description: messages.descriptionShippingMethods,
    newButton: messages.newButtonShippingMethods,
    createModalTitle: messages.createModalTitleShippingMethods,
  },
} as const

/**
 * Feature Set: PO Terms & Methods section — parameterized by `kind`, rendered
 * once per Shipping Terms / Payment Terms / Shipping Methods tab.
 *
 * Orchestrates:
 * - Kind-derived heading/description + "New {kind}" button (FS composition)
 * - The list feature, with per-row inline-edit cells injected via
 *   renderEditableCells (×2 per row) and row deletes delegated to the
 *   hook-only delete command (ADR-9)
 * - The FS-owned create ModalDialog wrapping the create form presenter
 *   (BHV-028)
 * - The CREATED/UPDATED indicator state spanning three features
 *   (BHV-029/030)
 */
export const POTermsMethodsView = ({ kind }: IPOTermsMethodsViewProps) => {
  const intl = useProcurifyIntl()

  const {
    states: { isCreateModalOpen, justCreatedId, justUpdatedId },
    methods: {
      openCreateModal,
      closeCreateModal,
      handleCreateSuccess,
      handleEditSuccess,
      onDeleteItem,
    },
  } = usePOTermsMethodsView(kind)

  const kindMessages = KIND_MESSAGES[kind]

  const renderEditableCells = (item: TermOrMethod) => ({
    name: (
      <EditableTermOrMethodTextFieldPresenter
        kind={kind}
        item={item}
        field='name'
        onSuccess={handleEditSuccess}
      />
    ),
    description: (
      <EditableTermOrMethodTextFieldPresenter
        kind={kind}
        item={item}
        field='description'
        onSuccess={handleEditSuccess}
      />
    ),
  })

  return (
    <>
      <Space>
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space size='small'>
            <Header as='h3'>{intl.formatMessage(kindMessages.heading)}</Header>
            <Text>{intl.formatMessage(kindMessages.description)}</Text>
          </Space>
          <PrimaryButton iconComponent={<IconAdd />} onClick={openCreateModal}>
            {intl.formatMessage(kindMessages.newButton)}
          </PrimaryButton>
        </Space>

        <TermsMethodsListPresenter
          // Remount on kind change so per-kind presenter state (pagination
          // page) resets — otherwise a stale page number 404s against the
          // next kind's shorter list
          key={kind}
          kind={kind}
          justCreatedId={justCreatedId}
          justUpdatedId={justUpdatedId}
          onDeleteItem={onDeleteItem}
          renderEditableCells={renderEditableCells}
        />
      </Space>

      {/* Create modal shell — owned by the Feature Set per ADR-9 (BHV-028) */}
      <ModalDialog
        title={intl.formatMessage(kindMessages.createModalTitle)}
        maxWidth='md'
        open={isCreateModalOpen}
        onClose={closeCreateModal}
      >
        {isCreateModalOpen && (
          <CreateTermOrMethodFormPresenter
            kind={kind}
            onSuccess={handleCreateSuccess}
            onCancel={closeCreateModal}
          />
        )}
      </ModalDialog>
    </>
  )
}

POTermsMethodsView.displayName = 'POTermsMethodsView'
